import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { useEventStore } from 'src/shared/store/event-store'

/**
 * Hook to extract GCLID and UTM parameters from URL and store them in eventStore
 * This should be used in the app's root component to initialize tracking
 */
export const useTrackingParams = () => {
  const location = useLocation()
  const { gclid, fbclid, utmParams, setGclid, setFbclid, setUtmParams } = useEventStore()

  useEffect(() => {
    // Parse the URL parameters
    const searchParams = new URLSearchParams(location.search)

    // Handle GCLID
    const urlGclid = searchParams.get('gclid')
    if (urlGclid && urlGclid !== gclid) {
      setGclid(urlGclid)
    }

    const urlFbclid = searchParams.get('fbclid')
    if (urlFbclid && urlFbclid !== fbclid) {
      setFbclid(urlFbclid)
    }

    // Handle UTM parameters - only collect them from URL, don't rely on current state
    const newUtmParams: Record<string, string | undefined> = {}
    let hasUtmParams = false

    // Check each UTM parameter in the URL
    const utmSource = searchParams.get('utm_source')
    if (utmSource !== null) {
      newUtmParams.source = utmSource || undefined
      hasUtmParams = true
    }

    const utmMedium = searchParams.get('utm_medium')
    if (utmMedium !== null) {
      newUtmParams.medium = utmMedium || undefined
      hasUtmParams = true
    }

    const utmCampaign = searchParams.get('utm_campaign')
    if (utmCampaign !== null) {
      newUtmParams.campaign = utmCampaign || undefined
      hasUtmParams = true
    }

    const utmTerm = searchParams.get('utm_term')
    if (utmTerm !== null) {
      newUtmParams.term = utmTerm || undefined
      hasUtmParams = true
    }

    const utmContent = searchParams.get('utm_content')
    if (utmContent !== null) {
      newUtmParams.content = utmContent || undefined
      hasUtmParams = true
    }

    const utmAd = searchParams.get('utm_ad')
    if (utmAd !== null) {
      newUtmParams.ad = utmAd || undefined
      hasUtmParams = true
    }

    // Only update if at least one UTM parameter is present in the URL
    if (hasUtmParams) {
      setUtmParams(newUtmParams)
    }
  }, [location.search, setGclid, setUtmParams, gclid]) // Only depend on URL and actions, not on state

  return { gclid, utmParams }
}
